import React, { useRef, useEffect, useState } from 'react';
import { 
    Box, 
    Typography, 
    Modal, 
    Paper, 
    Button,
    Stack,
    ToggleButton,
    ToggleButtonGroup
} from '@mui/material';
import {
    Draw as DrawIcon,
    AutoAwesome as AutoIcon
} from '@mui/icons-material';
import SignaturePad from 'react-signature-canvas';

const SignatureModalWithAuto = ({ 
    open, 
    onClose, 
    sigPadRef, 
    employeeName, 
    onAccept,
    existingSignature = null 
}) => {
    const [signatureMode, setSignatureMode] = useState('draw');
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const [isSignatureLoaded, setIsSignatureLoaded] = useState(false);
    const [wasCleared, setWasCleared] = useState(false);
    const [canvasWidth, setCanvasWidth] = useState(600);

    // Generate auto signature
    const generateAutoSignature = () => {
        if (!sigPadRef.current || !employeeName) return;
        
        const canvas = sigPadRef.current.getCanvas();
        const ctx = canvas.getContext('2d');
        
        // Clear canvas
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        
        // Set font style for signature
        ctx.font = 'italic 48px Brush Script MT, cursive';
        ctx.fillStyle = '#000000';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        
        // Draw the name as signature
        ctx.fillText(employeeName, canvas.width / 2, canvas.height / 2);
        
        // Add a simple underline
        ctx.beginPath();
        ctx.moveTo(50, canvas.height / 2 + 30);
        ctx.lineTo(canvas.width - 50, canvas.height / 2 + 30);
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 2;
        ctx.stroke();
    };

    // Handle mode change
    const handleModeChange = (event, newMode) => {
        if (newMode !== null) {
            setSignatureMode(newMode);
            
            if (newMode === 'auto') {
                // Generate auto signature when switching to auto mode
                setWasCleared(false);
                setTimeout(() => {
                    generateAutoSignature();
                }, 100);
            } else {
                // When switching to draw mode, only reload if not cleared by user
                if (sigPadRef.current && !wasCleared) {
                    if (existingSignature) {
                        setTimeout(() => {
                            try {
                                sigPadRef.current.fromDataURL(existingSignature);
                            } catch (e) {
                                console.error('Error reloading signature:', e);
                                sigPadRef.current.clear();
                            }
                        }, 100);
                    } else {
                        sigPadRef.current.clear();
                    }
                }
            }
        }
    };

    // Clear signature
    const clearSignature = () => {
        if (!sigPadRef.current) return;
        
        try {
            sigPadRef.current.clear();
            // Mark as cleared so it won't reload
            setWasCleared(true);
            setIsSignatureLoaded(false);
            
            // If in auto mode, regenerate
            if (signatureMode === 'auto') {
                setTimeout(() => {
                    generateAutoSignature();
                }, 100);
            }
        } catch (e) {
            console.error('Error clearing signature:', e);
        }
    };

    // Resize canvas to match container
    useEffect(() => {
        if (open && containerRef.current) {
            // Wait for modal to fully render
            setTimeout(() => {
                if (containerRef.current) {
                    const containerWidth = containerRef.current.offsetWidth;
                    // Canvas should fill the entire container
                    setCanvasWidth(containerWidth); 
                }
            }, 100);
        }
    }, [open]);

    // Load existing signature when modal opens
    useEffect(() => {
        if (!open) {
            // Reset states when closing
            setIsSignatureLoaded(false);
            setWasCleared(false);
            return;
        }
        
        // Reset to draw mode when opening
        setSignatureMode('draw');
        setWasCleared(false);
        
        // Load signature only once when opening
        const loadSignature = () => {
            if (!sigPadRef.current) {
                // Try again if ref not ready
                setTimeout(loadSignature, 50);
                return;
            }
            
            if (existingSignature && !isSignatureLoaded) {
                try {
                    // First set canvas dimensions to match container
                    const canvas = sigPadRef.current.getCanvas();
                    const currentWidth = containerRef.current ? containerRef.current.offsetWidth : canvas.width;
                    
                    // Load the image to check its original dimensions
                    const img = new Image();
                    img.onload = () => {
                        // Use fromDataURL with proper ratio
                        sigPadRef.current.fromDataURL(existingSignature, {
                            ratio: 1,
                            width: currentWidth,
                            height: 200
                        });
                        setIsSignatureLoaded(true);
                    };
                    img.onerror = () => {
                        // Fallback to direct loading
                        sigPadRef.current.fromDataURL(existingSignature);
                        setIsSignatureLoaded(true);
                    };
                    img.src = existingSignature;
                } catch (e) {
                    console.error('Error loading signature:', e);
                    sigPadRef.current.clear();
                }
            } else if (!existingSignature) {
                sigPadRef.current.clear();
            }
        };
        
        // Start loading after a short delay to ensure modal is rendered
        setTimeout(loadSignature, 150);
        
    }, [open]); // Only depend on open, not on other states

    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '90vw',
                maxWidth: 650,
                bgcolor: 'background.paper',
                borderRadius: 2,
                boxShadow: 24,
                p: 3,
            }}>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                    Signature for: {employeeName}
                </Typography>

                {/* Mode Selection */}
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        Signature Type:
                    </Typography>
                    <ToggleButtonGroup
                        value={signatureMode}
                        exclusive
                        onChange={handleModeChange}
                        size="small"
                    >
                        <ToggleButton value="draw">
                            <DrawIcon sx={{ mr: 1 }} />
                            Draw
                        </ToggleButton>
                        <ToggleButton value="auto">
                            <AutoIcon sx={{ mr: 1 }} />
                            Auto
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Stack>

                {/* Signature Pad */}
                <Box 
                    ref={containerRef}
                    sx={{ 
                        mb: 3,
                        bgcolor: signatureMode === 'auto' ? 'grey.50' : 'white',
                        border: '1px dashed #ccc',
                        borderRadius: 1,
                        p: 0,
                        display: 'block'
                    }}
                >
                    <SignaturePad 
                        ref={sigPadRef} 
                        canvasProps={{ 
                            width: canvasWidth, 
                            height: 200, 
                            style: { 
                                display: 'block',
                                cursor: signatureMode === 'auto' ? 'not-allowed' : 'crosshair'
                            }
                        }}
                        // Disable drawing in auto mode
                        onBegin={() => {
                            if (signatureMode === 'auto') {
                                return false;
                            }
                        }}
                    />
                </Box>

                {/* Action Buttons */}
                <Stack direction="row" spacing={2} justifyContent="flex-end">
                    <Button 
                        variant="outlined" 
                        onClick={clearSignature}
                    >
                        Clear
                    </Button>
                    <Button 
                        variant="contained" 
                        color="primary"
                        onClick={onAccept}
                    >
                        Accept
                    </Button>
                    <Button 
                        variant="outlined" 
                        color="inherit" 
                        onClick={onClose}
                    >
                        Cancel
                    </Button>
                </Stack>
            </Box>
        </Modal>
    );
};

export default SignatureModalWithAuto;